import { gql, type TypedDocumentNode } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { BodyL } from '@sorare/core/src/atoms/typography';
import { SecondaryPageBackground } from '@sorare/core/src/atoms/ui/SecondaryPageBackground';
import { Dialog } from '@sorare/core/src/components/dialog';
import { Header } from '@sorare/core/src/components/dialog/Header';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery.ts';
import { glossary } from '@sorare/core/src/lib/glossary';

import { useEnsureReferrerCode } from '#src/hooks/useEnsureReferrerCode.ts';
import { useInviteShareMessage } from '#src/hooks/useInviteShareMessage.ts';

import { InviteCTA } from '../InviteCTA/index.tsx';
import { InviteTitle } from '../InviteTitle/index.tsx';
import { InviteContent } from './InviteContent';
import { RewardsContent } from './RewardsContent/index.tsx';
import type {
  InviteFriendsDialogQuery,
  InviteFriendsDialogQueryVariables,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 1.5 })`
  position: relative;
  min-height: 100%;
`;
const HeaderWrapper = styled.div`
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 1;
`;
const FooterWrapper = styled(Vertical).attrs({ center: true })`
  width: 100%;
  position: sticky;
  bottom: var(--double-unit);
  z-index: 1;
  margin-top: auto;
`;

const Wrapper = styled(Vertical).attrs({
  gap: 4,
  center: true,
})`
  padding: var(--intermediate-unit) var(--double-unit);
`;

const INVITE_FRIENDS_DIALOG_QUERY = gql`
  query InviteFriendsDialogQuery($sport: Sport!) {
    cardSet {
      id
      maximumInviteTasksAsReferrer(sport: $sport)
      ...InviteContent_cardSetRoot
      ...RewardsContent_cardSetRoot
    }
    currentUser {
      slug
      referrerCode
      inviteLink(gameMode: SET, sport: $sport)
      inviteTasksAsReferrer(sport: $sport) {
        id
      }
    }
  }
  ${InviteContent.fragments.cardSetRoot}
  ${RewardsContent.fragments.cardSetRoot}
` as TypedDocumentNode<
  InviteFriendsDialogQuery,
  InviteFriendsDialogQueryVariables
>;

type Props = { open: boolean; onClose: () => void };
export const InviteDialog = ({ open, onClose }: Props) => {
  const { sport } = useSportContext();
  const [tab, setTab] = useState<'invite' | 'rewards'>('invite');
  const { data, loading } = useQuery(INVITE_FRIENDS_DIALOG_QUERY, {
    variables: {
      sport,
    },
  });

  const { currentUser, cardSet } = data || {};
  const { maximumInviteTasksAsReferrer } = cardSet || {};
  const { inviteTasksAsReferrer, referrerCode, inviteLink } = currentUser || {};
  const { generating } = useEnsureReferrerCode(referrerCode, {
    skip: loading || !currentUser,
  });
  const inviteShareMessage = useInviteShareMessage(referrerCode);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" hideHeader>
      <Root>
        <SecondaryPageBackground
          overlayStartColor="var(--c-button-secondary-surface)"
          overlayMidColor="var(--c-competition-contenter-secondary)"
          overlayEndColor="var(--c-competition-mls-tertiary)"
        />
        <HeaderWrapper>
          <Header
            onClose={onClose}
            title={
              <TabBar variant="primary" value={tab}>
                <Tab value="invite" onClick={() => setTab('invite')}>
                  <FormattedMessage {...glossary.invite} />
                </Tab>
                <Tab value="rewards" onClick={() => setTab('rewards')}>
                  <FormattedMessage {...glossary.rewards} />
                </Tab>
              </TabBar>
            }
          />
        </HeaderWrapper>
        <Wrapper>
          {tab === 'invite' && (
            <InviteContent cardSetRoot={cardSet} loading={loading} />
          )}
          {tab === 'rewards' && (
            <Vertical gap={3} style={{ minHeight: '50vh' }}>
              <Vertical center gap={1.5}>
                <InviteTitle color="var(--c-black)" />
                <BodyL className="text-center" color="var(--c-black)">
                  <FormattedMessage defaultMessage="Your friends must complete levels within 2 months" />
                </BodyL>
              </Vertical>
              <RewardsContent cardSetRoot={cardSet} />
            </Vertical>
          )}
          <FooterWrapper>
            <InviteCTA
              referrerCode={referrerCode}
              friendsRemaining={
                (maximumInviteTasksAsReferrer || 10) -
                (inviteTasksAsReferrer?.length || 0)
              }
              generating={generating}
              shareMessage={inviteShareMessage}
              shareUrl={inviteLink}
              forceMobileLayout
            />
          </FooterWrapper>
        </Wrapper>
      </Root>
    </Dialog>
  );
};
